import { queryOptions } from '@tanstack/vue-query'
import { type MaybeRefOrGetter, toValue } from 'vue'
import { fetchYield } from '@/features/assets/api/fetchYield'
import { fetchAsset } from '@/features/assets/api/fetchAsset'
import { fetchYieldActivityLog } from '@/features/assets/api/fetchYieldActivityLog'
import { fetchPaginatedAssets } from '@/features/assets/api/fetchPaginatedAssets'
import type { ComposableOptions, SortedField } from '@/types'
import { checkAssetCreationEligibility } from '@/features/assets/api/checkAssetCreationEligibility'

type AssetPaginationOptions = ComposableOptions<{ page: number, pageSize: number, filter: string, sorting: SortedField[], owner?: number }>

export namespace queryKeys {
  export namespace yields {
    export function all(): readonly ['asset-yields']
    export function all() {
      return ['asset-yields'] as const
    }

    export function details(): readonly ['asset-yields', 'details']
    export function details(assetCode: MaybeRefOrGetter<string>): readonly ['asset-yields', 'details', MaybeRefOrGetter<string>]
    export function details(assetCode?: MaybeRefOrGetter<string>) {
      return assetCode
        ? [...queryKeys.yields.all(), 'details', assetCode] as const
        : [...queryKeys.yields.all(), 'details'] as const
    }

    export function activityLog(): readonly ['asset-yields', 'activity']
    export function activityLog(assetCode: MaybeRefOrGetter<string>): readonly ['asset-yields', 'activity', MaybeRefOrGetter<string>]
    export function activityLog(assetCode?: MaybeRefOrGetter<string>) {
      return assetCode
        ? [...queryKeys.yields.all(), 'activity', assetCode] as const
        : [...queryKeys.yields.all(), 'activity'] as const
    }
  }

  export namespace assets {
    export function all(): readonly ['assets']
    export function all() {
      return ['assets'] as const
    }

    export function details(): readonly ['assets', 'details']
    export function details<T extends MaybeRefOrGetter<string|undefined>>(assetCode: T): readonly ['assets', 'details', T]
    export function details(assetCode?: MaybeRefOrGetter<string|undefined>) {
      return assetCode
        ? [...queryKeys.assets.all(), 'details', assetCode] as const
        : [...queryKeys.assets.all(), 'details'] as const
    }

    export function paginated(): readonly ['assets', 'paginated']
    export function paginated(options: AssetPaginationOptions): readonly ['assets', 'paginated', AssetPaginationOptions]
    export function paginated(options?: AssetPaginationOptions) {
      return options
        ? [...queryKeys.assets.all(), 'paginated', options] as const
        : [...queryKeys.assets.all(), 'paginated'] as const
    }

    export function creationEligibilityLookup(): readonly ['assets', 'eligibility-lookup']
    export function creationEligibilityLookup<T extends MaybeRefOrGetter<number|undefined>>(projectCode: T): readonly ['assets', 'eligibility-lookup', T]
    export function creationEligibilityLookup(projectCode?: MaybeRefOrGetter<number|undefined>) {
      return projectCode
        ? [...queryKeys.assets.all(), 'eligibility-lookup', projectCode] as const
        : [...queryKeys.assets.all(), 'eligibility-lookup'] as const
    }
  }
}

export const YieldQueries = {
  all: () => queryOptions({ queryKey: queryKeys.yields.all() }),

  fetch: (assetCode: MaybeRefOrGetter<string>) => queryOptions({
    queryKey: queryKeys.yields.details(assetCode),
    queryFn: () => fetchYield(toValue(assetCode)),
    meta: {
      logger: 'features/assets/queries/YieldQueries.fetch'
    }
  }),

  activityLog: (assetCode: MaybeRefOrGetter<string>) => queryOptions({
    queryKey: queryKeys.yields.activityLog(assetCode),
    queryFn: () => fetchYieldActivityLog(toValue(assetCode))
  })
} as const

export const AssetQueries = {
  fetch: (assetCode: MaybeRefOrGetter<string|undefined>) => queryOptions({
    enabled: () => toValue(assetCode) !== undefined,
    queryKey: queryKeys.assets.details(assetCode),
    queryFn: () => fetchAsset(toValue(assetCode)!),
    meta: {
      logger: 'features/assets/queries/AssetQueries.fetch'
    }
  }),
} as const

export const fetchPaginatedAssetsQueryOptions = (options: AssetPaginationOptions) => queryOptions({
  queryKey: queryKeys.assets.paginated(options),
  queryFn: () => fetchPaginatedAssets({
    page: toValue(options.page),
    pageSize: toValue(options.pageSize),
    filter: toValue(options.filter),
    sorting: toValue(options.sorting),
    owner: toValue(options.owner)
  })
})

export const checkAssetCreationEligibilityQueryOptions = (projectCode: MaybeRefOrGetter<number|undefined>) => queryOptions({
  queryKey: queryKeys.assets.creationEligibilityLookup(projectCode),
  enabled: () => typeof toValue(projectCode) === 'number',
  queryFn: () => checkAssetCreationEligibility(toValue(projectCode)!)
})
